import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { QueryBuilderComponent, RuleChangeEventArgs, RuleModel, QueryBuilderModule, QueryLibraryService } from '@syncfusion/ej2-angular-querybuilder';
import { Subscription } from 'rxjs';
import { AssetStorageEntryLicense, FilterCondition, FilterDTO, FilterOperator, ImageQuality } from 'src/app/base/models/models.swagger.generated';
import { EnumTranslationService } from 'src/app/base/services/enum-translation.service';
import { SyncfusionTranslationService } from 'src/app/base/services/syncfusion-translation.service';
import { DropDownConstantsFactory } from 'src/app/base/shared/syncfusion/dropdown-constants-factory';
import { AssetConfigurationsService } from '../../asset-configurations.service';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { GridModule } from '@syncfusion/ej2-angular-grids';

@Component({
    selector: 'app-asset-storage-filter',
    templateUrl: './asset-storage-filter.component.html',
    styleUrls: ['./asset-storage-filter.component.scss'],
    imports: [DialogModule, QueryBuilderModule, GridModule, DropDownListModule, TranslateModule],
    providers: [QueryLibraryService]
})
export class AssetStorageFilterComponent implements OnInit,AfterViewInit, OnDestroy {

  open() {
    this.assetFilterDialog?.show();
  }

  @Input() public set filterDTO(filter: FilterDTO) {
    this.setInitialRuleModel(filter);
  }

  @Output() public filterUpdated: EventEmitter<FilterDTO> = new EventEmitter<FilterDTO>();
  @ViewChild('querybuilder') public qryBldrObj?: QueryBuilderComponent;
  @ViewChild('assetFilterDialog') public assetFilterDialog?: DialogComponent;

  public showButtons: Object = { ruleDelete: true, groupInsert: true, groupDelete: true };

  private _filterDTO: FilterDTO;
  private filterOperator = FilterOperator;
  public importRules?: RuleModel;
  public allowedOperatorsString = [
    {
      "value": "startswith",
      "key": "Starts With"
    },
    {
      "value": "endswith",
      "key": "Ends With"
    },
    {
      "value": "contains",
      "key": "Contains"
    },
    {
      "value": "equal",
      "key": "Equal"
    },
    {
      "value": "notequal",
      "key": "Not Equal"
    }
  ];
  public allowedOperatorsEnum = [
    {
      "value": "equal",
      "key": "Equal"
    },
    {
      "value": "notequal",
      "key": "Not Equal"
    },
  ];
  private subscription: Subscription;

  public languages: string[] = [];

  public imageQualities = this.enumTranslateService.translateNumericEnum(ImageQuality, 'i18n.shared.ImageQualities.');

  public enumDropdownSettings = DropDownConstantsFactory.createNumericEnumDropdownSettings();

  public licenses = this.enumTranslateService.translateNumericEnum(AssetStorageEntryLicense, 'i18n.shared.AssetStorageEntryLicenses.');
  public fields = { text: 'text', value: 'value' };

  ruleValueChange(e: any, ruleID: string): void {
    let elem: HTMLElement = document.getElementById(ruleID)!.querySelector('.e-rule-value') as HTMLElement;
    this.qryBldrObj!.notifyChange(e.value as string, elem, 'value');
  }
  constructor(private translateService: TranslateService,
    private syncfusionTranslationService: SyncfusionTranslationService,
    private enumTranslateService: EnumTranslationService,
    private assetConfigurationsService: AssetConfigurationsService) { }

  ngOnInit(): void {
    this.subscription = this.syncfusionTranslationService.onLanguageChanged.subscribe(() => {
      setTimeout(() => {
        this.translateOperators();
        this.qryBldrObj?.refresh();
      }, 1000);
    });
    const config = this.assetConfigurationsService.assetConfiguration;
    if (config) {
      this.languages = config.supportedAssetLanguages;
    }
  }

  ngAfterViewInit(): void {
    this.translateOperators();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private setInitialRuleModel(filter: FilterDTO) {
    const fieldTypes = {};
    fieldTypes['ImageQuality'] = 'string';
    fieldTypes['License'] = 'string';
    fieldTypes['Source'] = 'string';
    fieldTypes['LanguageCodeIso3'] = 'string';
    fieldTypes['AssignedKeyWords.AssetStorageKeyWord.Name'] = 'string';
    this.qryBldrObj?.reset();
    if (filter) {
      var ruleModel = {
        'condition': filter.condition === FilterCondition.Or ? 'or' : 'and',
        'rules': []
      }
      ruleModel.rules = this.getRules(filter, fieldTypes);
      this.importRules = ruleModel;
      this.qryBldrObj?.setRules(ruleModel);
    }
    else {
      this.importRules = null;
    };

  }

  private getRules(filter: FilterDTO, fieldTypes: {}): RuleModel[] {
    if (filter.predicates?.length > 0) {
      var ruleModels = [];
      filter.predicates.forEach(p => {
        var ruleModel = {
          'label': p.field,
          'field': p.field,
          'type': p.field ? fieldTypes[p.field] : '',
          'operator': p.operator ? FilterOperator[p.operator].toLocaleLowerCase() : 'equal',
          'value': p.value,
          'rules': []
        };
        if (p.predicates?.length > 0) {
          ruleModel.rules = this.getRules(p, fieldTypes);
        }
        ruleModels.push(ruleModel);
      });
      return ruleModels;
    }
    return null;
  }

  public onRuleChange(args: RuleChangeEventArgs) {
    if (this.qryBldrObj.rule.rules?.length > 0) {
      if (!this._filterDTO) {
        this._filterDTO = new FilterDTO();
        this._filterDTO.condition = FilterCondition.Or;
        this._filterDTO.isComplex = true;
        // add the first predicate with IsFile = false
        var predicate = new FilterDTO();
        predicate.condition = FilterCondition.Or;
        predicate.isComplex = false;
        predicate.operator = FilterOperator.NotEqual;
        predicate.field = 'IsFile';
        predicate.value = 'true';
        this._filterDTO.predicates = [predicate];
        let otherSelectedFilter = new FilterDTO();
        otherSelectedFilter.condition = this.qryBldrObj.rule.condition === 'or' ? FilterCondition.Or : FilterCondition.And;
        otherSelectedFilter.isComplex = true;
        otherSelectedFilter.predicates = [];
        this._filterDTO.predicates.push(otherSelectedFilter);
      }
      if (this.qryBldrObj.rule) {
        let predicates = this.getPredicates(this.qryBldrObj.rule);
        if (predicates?.length > 0) {
          this._filterDTO.predicates[1].predicates = predicates;
          this._filterDTO.predicates[1].condition = this.qryBldrObj.rule.condition === 'or' ? FilterCondition.Or : FilterCondition.And;
        }
      }
    }
    else {
      this.qryBldrObj?.reset();
      this._filterDTO = null;
      this.filterUpdated.emit(this._filterDTO);
    }
  }

  private getPredicates(ruleModel: RuleModel): FilterDTO[] {
    var predicates = [];
    if (ruleModel.rules) {
      ruleModel.rules.forEach(rule => {
        var f = new FilterDTO();
        let condition = rule.condition ? rule.condition : ruleModel.condition;
        f.condition = condition === 'or' ? FilterCondition.Or : FilterCondition.And;

        f.operator = this.getDTOOperatorForUIOperator(rule.operator);
        f.field = rule.field;
        f.isComplex = false;
        f.value = rule.value?.toString();
        if (rule.rules?.length > 0) {
          f.isComplex = true;
          f.predicates = this.getPredicates(rule);
        }
        predicates.push(f);
      });

    }
    return predicates;
  }

  private getDTOOperatorForUIOperator(uiOperator: string): FilterOperator {
    if (uiOperator) {
      for (var k in this.filterOperator) {
        if (this.filterOperator[k].toLowerCase() == uiOperator) {
          return this.filterOperator[k] as any as FilterOperator;
        }
      }
    }
    return null;
  }

  private translateOperators() {
    this.allowedOperatorsString = [
      {
        "value": "startswith",
        "key": this.translateService.instant('i18n.domain.assets.asset-storage.filter.operators.startswith')
      },
      {
        "value": "endswith",
        "key": this.translateService.instant('i18n.domain.assets.asset-storage.filter.operators.endswith')
      },
      {
        "value": "contains",
        "key": this.translateService.instant('i18n.domain.assets.asset-storage.filter.operators.contains')
      },
      {
        "value": "equal",
        "key": this.translateService.instant('i18n.domain.assets.asset-storage.filter.operators.equal')
      },
      {
        "value": "notequal",
        "key": this.translateService.instant('i18n.domain.assets.asset-storage.filter.operators.notequal')
      }
    ];
    this.allowedOperatorsEnum = [
      {
        "value": "equal",
        "key": this.translateService.instant('i18n.domain.assets.asset-storage.filter.operators.equal')
      },
      {
        "value": "notequal",
        "key": this.translateService.instant('i18n.domain.assets.asset-storage.filter.operators.notequal')
      },
    ]
  }

  public onApplyFilter() {
    this.onRuleChange({ rule: this.qryBldrObj.rule });
    this.filterUpdated.emit(this._filterDTO);
    this.assetFilterDialog?.hide();
  }

  public onCancelFilter() {
    this.assetFilterDialog?.hide();
  }

  public filterChange(e: any, ruleID: string): void {
    let elem: HTMLElement = document.getElementById(ruleID)!.querySelector('.e-rule-value') as HTMLElement;
    this.qryBldrObj!.notifyChange(e.value as string, elem, 'value');
  }

  public clearFilter() {
    this.qryBldrObj?.reset();
    this._filterDTO = null;
    this.filterUpdated.emit(this._filterDTO);
    this.assetFilterDialog?.hide();
  }
}
